/**
 * @vuelto-surface
 * mobile: ok
 * desktop: ok
 * strategy: unified
 */
import React, { type RefObject } from 'react';
import { ScrollView, Text, TextInput, TouchableOpacity, View, type StyleProp, type ViewStyle } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import { colors, ticketColors } from '../theme/public-api';
import { operationalFilterStyles as styles } from './operationalFilters.styles';

export function OperationalFilterFieldLabel({ children }: { children: string }) {
  return <Text style={styles.fieldLabel}>{children}</Text>;
}

export function OperationalFilterSectionDivider() {
  return <View style={styles.sectionDivider} accessibilityElementsHidden />;
}

type SearchProps = {
  value: string;
  onChangeText: (value: string) => void;
  placeholder: string;
  autoCapitalize?: 'none' | 'words' | 'sentences';
  inputRef?: RefObject<TextInput | null>;
  /** Desk toolbar (Catálogo/POS ticket line). */
  appearance?: 'default' | 'desk';
  /** Alinea la altura a los botones de toolbar (44) cuando va junto a acciones. */
  dense?: boolean;
  /** Override puntual del wrap (fondo/borde) sin duplicar el campo. */
  boxStyle?: StyleProp<ViewStyle>;
  testID?: string;
  accessibilityLabel?: string;
};

export function OperationalFilterSearchField({
  value,
  onChangeText,
  placeholder,
  autoCapitalize = 'words',
  inputRef,
  appearance = 'default',
  dense = false,
  boxStyle,
  testID,
  accessibilityLabel,
}: SearchProps) {
  const desk = appearance === 'desk';
  return (
    <View style={[styles.searchWrap, desk && styles.searchWrapDesk, desk && dense && styles.searchWrapDeskDense, boxStyle]}>
      <Ionicons
        name="search"
        size={18}
        color={desk ? ticketColors.ink3 : colors.textMuted}
        style={styles.searchIcon}
      />
      <TextInput
        ref={inputRef}
        style={[styles.searchInput, desk && styles.searchInputDesk, desk && dense && styles.searchInputDeskDense]}
        value={value}
        onChangeText={onChangeText}
        placeholder={placeholder}
        placeholderTextColor={desk ? ticketColors.ink3 : colors.inputPlaceholder}
        autoCapitalize={autoCapitalize}
        autoCorrect={false}
        testID={testID}
        accessibilityLabel={accessibilityLabel}
      />
      {value ? (
        <TouchableOpacity
          onPress={() => onChangeText('')}
          style={styles.clearSearchBtn}
          accessibilityLabel="Limpiar búsqueda"
          accessibilityRole="button"
        >
          <Ionicons name="close-circle" size={18} color={desk ? ticketColors.ink2 : colors.textSecondary} />
        </TouchableOpacity>
      ) : null}
    </View>
  );
}

export type OperationalFilterOption<T extends string> = {
  value: T;
  label: string;
  shortLabel?: string;
  accessibilityLabel?: string;
};

type SegmentedProps<T extends string> = {
  options: Array<OperationalFilterOption<T>>;
  value: T;
  onChange: (value: T) => void;
  /** Brand selected state on ticket surfaces. */
  ticketTone?: boolean;
  /** soft = chip seleccionado suave; solid = botón primario dentro del grupo. */
  selectedStyle?: 'soft' | 'solid';
  /** stretch = segmentos reparten ancho; hug = ancho al contenido (settings). */
  layout?: 'stretch' | 'hug';
  /** Segmentos de 34px para alinear la píldora a controles de toolbar de 44. */
  dense?: boolean;
  /** Si está set, cada segmento recibe testID=`${prefix}.${option.value}`. */
  testIDPrefix?: string;
};

export function OperationalFilterSegmentedRow<T extends string>({
  options,
  value,
  onChange,
  ticketTone = false,
  selectedStyle = 'soft',
  layout = 'stretch',
  dense = false,
  testIDPrefix,
}: SegmentedProps<T>) {
  return (
    <View
      style={[
        styles.segmentedRow,
        ticketTone && styles.segmentedRowTicket,
        layout === 'hug' && styles.segmentedRowHug,
      ]}
    >
      {options.map((option, index) => {
        const active = value === option.value;
        const isLast = index === options.length - 1;
        return (
          <TouchableOpacity
            key={option.value}
            style={[
              styles.segment,
              ticketTone && styles.segmentTicket,
              ticketTone && dense && styles.segmentTicketDense,
              layout === 'hug' && styles.segmentHug,
              active &&
                (ticketTone
                  ? selectedStyle === 'solid'
                    ? styles.segmentActiveTicketSolid
                    : styles.segmentActiveTicket
                  : styles.segmentActive),
              !ticketTone && isLast && styles.segmentLast,
            ]}
            onPress={() => onChange(option.value)}
            accessibilityRole="button"
            accessibilityLabel={option.accessibilityLabel ?? option.label}
            accessibilityState={{ selected: active }}
            testID={testIDPrefix ? `${testIDPrefix}.${option.value}` : undefined}
          >
            <Text
              style={[
                styles.segmentText,
                ticketTone && styles.segmentTextTicket,
                active &&
                  (ticketTone
                    ? selectedStyle === 'solid'
                      ? styles.segmentTextActiveTicketSolid
                      : styles.segmentTextActiveTicket
                    : styles.segmentTextActive),
              ]}
              numberOfLines={1}
            >
              {option.shortLabel ?? option.label}
            </Text>
          </TouchableOpacity>
        );
      })}
    </View>
  );
}

type ChipsProps<T extends string> = {
  options: Array<OperationalFilterOption<T>>;
  value: T;
  onChange: (value: T) => void;
  disabled?: boolean;
  /** Brand-solid selected state on ticket surfaces. */
  ticketTone?: boolean;
  /** wrap = fila con wrap; hug = chips no estiran el contenedor. */
  layout?: 'wrap' | 'hug';
  /** Si está set, cada chip recibe testID=`${prefix}.${option.value}`. */
  testIDPrefix?: string;
};

function OperationalFilterChipButton<T extends string>({
  option,
  active,
  disabled = false,
  ticketTone = false,
  testID,
  onPress,
}: {
  option: OperationalFilterOption<T>;
  active: boolean;
  disabled?: boolean;
  ticketTone?: boolean;
  testID?: string;
  onPress: () => void;
}) {
  return (
    <TouchableOpacity
      style={[
        styles.chip,
        ticketTone && styles.chipTicket,
        active && (ticketTone ? styles.chipActiveTicket : styles.chipActive),
        disabled && styles.chipDisabled,
      ]}
      onPress={onPress}
      disabled={disabled}
      accessibilityRole="button"
      accessibilityLabel={option.accessibilityLabel ?? option.label}
      accessibilityState={{ selected: active, disabled }}
      testID={testID}
    >
      <Text
        style={[
          styles.chipText,
          ticketTone && styles.chipTextTicket,
          active && (ticketTone ? styles.chipTextActiveTicket : styles.chipTextActive),
        ]}
        numberOfLines={1}
      >
        {option.label}
      </Text>
    </TouchableOpacity>
  );
}

export function OperationalFilterChipGroup<T extends string>({
  options,
  value,
  onChange,
  disabled = false,
  ticketTone = false,
  layout = 'wrap',
  testIDPrefix,
}: ChipsProps<T>) {
  return (
    <View style={[styles.chipsWrap, layout === 'hug' && styles.chipsWrapHug]}>
      {options.map((option) => {
        const active = value === option.value;
        return (
          <OperationalFilterChipButton
            key={option.value}
            option={option}
            active={active}
            disabled={disabled}
            ticketTone={ticketTone}
            testID={testIDPrefix ? `${testIDPrefix}.${option.value}` : undefined}
            onPress={() => {
              if (!disabled) onChange(option.value);
            }}
          />
        );
      })}
    </View>
  );
}

/** Mobile / muchas opciones: chips en fila horizontal sin wrap (período, vista rápida stock). */
export function OperationalFilterChipScrollRow<T extends string>({
  options,
  value,
  onChange,
  ticketTone = false,
  testIDPrefix,
}: ChipsProps<T>) {
  return (
    <ScrollView
      horizontal
      showsHorizontalScrollIndicator={false}
      keyboardShouldPersistTaps="handled"
      contentContainerStyle={styles.chipScrollContent}
    >
      {options.map((option) => {
        const active = value === option.value;
        return (
          <View key={option.value} style={styles.chipScrollItem}>
            <OperationalFilterChipButton
              option={option}
              active={active}
              ticketTone={ticketTone}
              testID={testIDPrefix ? `${testIDPrefix}.${option.value}` : undefined}
              onPress={() => onChange(option.value)}
            />
          </View>
        );
      })}
    </ScrollView>
  );
}

type PickerProps = {
  label: string;
  valueLabel: string;
  onPress: () => void;
  accessibilityLabel?: string;
  active?: boolean;
  /** Proto ticket: label above value. */
  layout?: 'inline' | 'stack';
  testID?: string;
};

export function OperationalFilterPickerRow({
  label,
  valueLabel,
  onPress,
  accessibilityLabel,
  active = false,
  layout = 'inline',
  testID,
}: PickerProps) {
  if (layout === 'stack') {
    return (
      <TouchableOpacity
        style={[styles.pickerStackRow, active && styles.pickerStackRowActive]}
        onPress={onPress}
        accessibilityRole="button"
        accessibilityLabel={accessibilityLabel ?? `${label}, ${valueLabel}`}
        testID={testID}
      >
        <View style={styles.pickerStackMeta}>
          <Text style={styles.pickerStackLabel}>{label}</Text>
          <Text
            style={[styles.pickerStackValue, !active && styles.pickerStackValueMuted]}
            numberOfLines={1}
          >
            {valueLabel}
          </Text>
        </View>
        <Ionicons
          name="chevron-forward"
          size={16}
          color={ticketColors.ink3}
          style={styles.pickerChevIcon}
        />
      </TouchableOpacity>
    );
  }

  return (
    <TouchableOpacity
      style={[styles.pickerRow, active && styles.pickerRowActive]}
      onPress={onPress}
      accessibilityRole="button"
      accessibilityLabel={accessibilityLabel ?? `${label}, ${valueLabel}`}
      testID={testID}
    >
      <Text style={styles.pickerLabel}>{label}</Text>
      <Text style={styles.pickerValue} numberOfLines={1}>
        {valueLabel}
      </Text>
      <Text style={styles.pickerChev}>▾</Text>
    </TouchableOpacity>
  );
}

type AvailabilityProps = {
  selected: boolean;
  onPress: () => void;
  disabled?: boolean;
  title?: string;
  hint?: string;
};

export function OperationalFilterAvailabilityRow({
  selected,
  onPress,
  disabled = false,
  title = 'Solo con stock',
  hint = 'Oculta ítems sin existencias en la sucursal',
}: AvailabilityProps) {
  return (
    <TouchableOpacity
      style={[styles.availabilityRow, selected && styles.availabilityRowOn, disabled && { opacity: 0.55 }]}
      onPress={onPress}
      disabled={disabled}
      accessibilityRole="switch"
      accessibilityState={{ checked: selected, disabled }}
      accessibilityLabel={title}
      accessibilityHint={hint}
    >
      <View style={styles.availabilityCopy}>
        <Text style={styles.availabilityTitle}>{title}</Text>
        {hint ? <Text style={styles.availabilityHint}>{hint}</Text> : null}
      </View>
      <View style={[styles.availabilitySwitch, selected && styles.availabilitySwitchOn]}>
        <View style={[styles.availabilitySwitchKnob, selected && styles.availabilitySwitchKnobOn]} />
      </View>
    </TouchableOpacity>
  );
}
